/* initials-editor.js (Claude) — the cart-drawer initials editor, moved out of
   assets/cart.js verbatim (file-size split), including the Jul-30 smoothness rework:
   optimistic paint + collapse on save, no drawer section swap, no cartUpdate republish. */
/* ============ Cart item initials editor ============ */
(function () {
  const SAVED_MESSAGE = 'Saved ✓';
  const ERROR_MESSAGE = 'Try again';

  // Capture a NATIVE fetch via a hidden iframe, BEFORE any third-party app (Fast Bundle, etc.)
  // gets a chance to override window.fetch on this page. Fast Bundle's bap_override_fetch
  // intercepts cart requests and can re-mangle or no-op POSTs to /cart/change.js for bundle
  // items, silently breaking property updates. Using the native fetch bypasses that.
  let nativeFetch = null;
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    // Use an about:blank src so the iframe inherits same-origin and we can read its window
    iframe.src = 'about:blank';
    (document.body || document.documentElement).appendChild(iframe);
    if (iframe.contentWindow && typeof iframe.contentWindow.fetch === 'function') {
      nativeFetch = iframe.contentWindow.fetch.bind(window);
    }
    // Leave the iframe in the DOM — removing it invalidates the captured reference.
  } catch (e) {
    // If iframe capture fails for any reason, fall back to window.fetch.
    nativeFetch = null;
  }
  // Always prefer the native reference; fall back to window.fetch only if capture failed.
  function cartFetch(url, opts) {
    if (nativeFetch) return nativeFetch(url, opts);
    return fetch(url, opts);
  }

  function findCartItemsHost(inputEl) {
    return inputEl.closest('cart-drawer-items') || inputEl.closest('cart-items') || null;
  }

  function getSectionsForHost(host) {
    if (!host) return [];
    if (typeof host.getSectionsToRender === 'function') {
      try {
        return host.getSectionsToRender();
      } catch (e) {
        return [];
      }
    }
    return [];
  }

  function setStatus(statusEl, text, isError) {
    if (!statusEl) return;
    statusEl.textContent = text || '';
    statusEl.classList.remove('is-success', 'is-error');
    if (!text) return;
    statusEl.classList.add(isError ? 'is-error' : 'is-success');
  }

  function getSectionInnerHTMLFromText(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  function replaceSectionsFromState(state, sectionsToRender) {
    if (!state || !state.sections) return;
    sectionsToRender.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;
      const target = container.querySelector(section.selector) || container;
      const newHTML = getSectionInnerHTMLFromText(state.sections[section.section] || '', section.selector);
      if (newHTML) target.innerHTML = newHTML;
    });
  }

  function saveInitials(inputEl) {
    const wrapper = inputEl.closest('.cart-item__initials-edit');
    if (!wrapper) return;
    const statusEl = wrapper.querySelector('.cart-item__initials-status');
    const line = parseInt(inputEl.dataset.line, 10);
    if (!line || Number.isNaN(line)) return;

    const initialsKey = wrapper.dataset.initialsKey || 'Add Initials (Optional)';
    let newValue = (inputEl.value || '').toUpperCase().trim();
    const originalValue = (inputEl.dataset.original || '').toUpperCase().trim();

    /* LENGTH GUARD (Claude, Aug 2026): maxlength only constrains TYPING and PASTING — a
       value written programmatically, or dropped in by browser autofill, sails straight
       past it. That is how order #66129 shipped a full first name in a 2-character field.
       This is the one choke point every drawer save passes through, so clamp here, against
       the field's OWN declared limit rather than a hard-coded 2, and keep the visible input
       in sync so the shopper sees exactly what will be engraved. */
    const initialsLimit = parseInt(inputEl.getAttribute('maxlength'), 10);
    if (initialsLimit > 0 && newValue.length > initialsLimit) {
      newValue = newValue.slice(0, initialsLimit);
      inputEl.value = newValue;
    }

    if (newValue === originalValue) {
      // No change — just collapse the editor
      if (newValue && wrapper && !wrapper.classList.contains('is-collapsed')) {
        wrapper.classList.add('is-collapsed');
        const cartItem = wrapper.closest('.cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]');
        const editBtn = cartItem && cartItem.querySelector('[data-toggles-initials="true"]');
        if (editBtn) editBtn.setAttribute('aria-expanded', 'false');
      }
      return;
    }

    setStatus(statusEl, 'Saving…', false);
    inputEl.disabled = true;

    /* SMOOTHNESS: optimistic UI. Paint the typed value and collapse the editor NOW so
       the drawer reflects the edit instantly; the network round-trip happens behind a UI
       that has already moved on. preorder-labels.js reads __scInitialsPending so its cart
       re-syncs never repaint a stale value over this while the save is in flight. */
    window.__scInitialsPending = window.__scInitialsPending || {};
    window.__scInitialsPending[line] = { value: newValue, ts: Date.now() };
    const rowEl = inputEl.closest('.cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]');
    function paintDisplay(value) {
      if (!rowEl) return;
      let d = rowEl.querySelector('.cart-item__initials-display');
      if (!value) { if (d) d.remove(); return; }
      if (!d) {
        d = document.createElement('div');
        d.className = 'cart-item__initials-display product-option d-flex align-items-center font-15 leading-16 spacing-0 text-0F0F0F font-light';
        const hostEl = rowEl.querySelector('.cart-item__initials-edit');
        if (hostEl && hostEl.parentNode) hostEl.parentNode.insertBefore(d, hostEl);
      }
      d.innerHTML = '';
      const lb = document.createElement('span');
      lb.className = 'd-flex font-semibold';
      lb.innerHTML = 'Add Initials (Optional):&nbsp;';
      const vv = document.createElement('span');
      vv.textContent = value;
      d.appendChild(lb);
      d.appendChild(vv);
    }
    paintDisplay(newValue);
    wrapper.classList.add('is-collapsed');
    const editBtnEl = rowEl && rowEl.querySelector('[data-toggles-initials="true"]');
    if (editBtnEl) editBtnEl.setAttribute('aria-expanded', 'false');

    const host = findCartItemsHost(inputEl);
    const sectionsToRender = getSectionsForHost(host);
    const sectionNames = sectionsToRender.map((s) => s.section);

    // Step 1: Fetch current cart to snapshot the line item we're editing
    cartFetch(`/cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const lineIndex0 = line - 1;
        const lineItem = cart && cart.items && cart.items[lineIndex0];
        if (!lineItem) throw new Error('Cart line ' + line + ' not found');

        const variantId = lineItem.variant_id || lineItem.id;
        const quantity = lineItem.quantity || 1;
        const sellingPlan = lineItem.selling_plan_allocation
          && lineItem.selling_plan_allocation.selling_plan
          && lineItem.selling_plan_allocation.selling_plan.id;

        // Start with existing properties (preserves ALL of them including underscore-prefixed)
        const newProps = Object.assign({}, lineItem.properties || {});
        newProps[initialsKey] = newValue;
        // Clean up any empty-string properties to avoid Shopify rejecting them
        Object.keys(newProps).forEach((k) => {
          if (newProps[k] === '' || newProps[k] === null || newProps[k] === undefined) {
            delete newProps[k];
          }
        });

        // Step 2: Use /cart/change.js with line + full properties (safest; preserves quantity)
        const cfg = typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        };
        const body = JSON.stringify({
          line: line,
          quantity: quantity,
          properties: newProps,
          sections: sectionNames,
          sections_url: window.location.pathname,
        });

        return cartFetch(`/cart/change.js`, Object.assign({}, cfg, { body: body }))
          .then((r) => r.text())
          .then((text) => {
            let parsed;
            try { parsed = JSON.parse(text); } catch (e) { parsed = null; }
            if (!parsed) throw new Error('Invalid response from /cart/change.js');
            if (parsed.status && parsed.status >= 400) throw new Error(parsed.description || 'Shopify error ' + parsed.status);

            // VERIFY the save actually persisted (don't trust — verify)
            return cartFetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then((r) => r.json())
              .then((verifyCart) => {
                const verifyItem = verifyCart.items && verifyCart.items[lineIndex0];
                const savedValue = verifyItem && verifyItem.properties && verifyItem.properties[initialsKey];
                if ((savedValue || '') !== newValue) {
                  console.error('[initials-editor] Save did NOT persist. Expected:', newValue, 'Got:', savedValue, 'Full line item:', verifyItem);
                  throw new Error('Save did not persist');
                }
                return parsed;
              });
          });
      })
      .then(() => {
        /* SMOOTHNESS: NO section re-render on success. An initials edit changes no price,
           quantity or item count, so swapping .drawer__inner wholesale (images and all)
           only produced the big visible flicker — and republishing cartUpdate made every
           cart-watching app re-fetch the cart for nothing. The optimistic paint above is
           already showing the final state; the same nodes are simply confirmed here. */
        /* Keep the pending marker (refreshed) instead of deleting it: a /cart.js read
           issued BEFORE this save landed can still be processing, and without the marker
           its stale value would repaint over the confirmed one. preorder-labels retires
           the marker as soon as a fresh read agrees with it (or after its 10s TTL). */
        window.__scInitialsPending[line] = { value: newValue, ts: Date.now() };
        inputEl.disabled = false;
        inputEl.value = newValue;
        inputEl.dataset.original = newValue;
        paintDisplay(newValue);
        setStatus(statusEl, SAVED_MESSAGE, false);
        setTimeout(() => setStatus(statusEl, '', false), 2000);
      })
      .catch((err) => {
        try { console.error('[initials-editor] save failed:', err); } catch (e) {}
        /* Roll the optimistic UI back: restore the display to the last saved value and
           reopen the editor with the typed value so the shopper can retry. */
        delete window.__scInitialsPending[line];
        paintDisplay(originalValue);
        wrapper.classList.remove('is-collapsed');
        if (editBtnEl) editBtnEl.setAttribute('aria-expanded', 'true');
        // Show the actual error so we can debug without DevTools
        setStatus(statusEl, (err && err.message) ? ('Error: ' + err.message) : ERROR_MESSAGE, true);
        inputEl.disabled = false;
        setTimeout(() => setStatus(statusEl, '', false), 6000);
      });
  }

  // Event delegation — works for both initial render and re-rendered items after section replacement.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target && target.classList && target.classList.contains('cart-item__initials-input')) {
      saveInitials(target);
    }
  });

  document.addEventListener('keydown', (event) => {
    const target = event.target;
    if (event.key === 'Enter' && target && target.classList && target.classList.contains('cart-item__initials-input')) {
      event.preventDefault();
      target.blur(); // triggers change
    }
  });

  // Live-uppercase as the user types, and auto-save when 2 valid chars are entered
  let autoSaveTimers = new WeakMap();
  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target && target.classList && target.classList.contains('cart-item__initials-input'))) return;

    const pos = target.selectionStart;
    const upper = (target.value || '').toUpperCase();
    if (upper !== target.value) {
      target.value = upper;
      try { target.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    }

    // Debounced auto-save once the customer has typed 2 characters.
    // Clear any pending timer first so fast typing / corrections reset the clock.
    const existingTimer = autoSaveTimers.get(target);
    if (existingTimer) clearTimeout(existingTimer);

    const trimmed = (target.value || '').trim();
    const original = (target.dataset.original || '').toUpperCase().trim();

    // Only auto-save when the value is complete (length 2) and different from the original.
    // This avoids saving half-typed "A" or firing repeatedly for the same value.
    if (trimmed.length === 2 && trimmed !== original) {
      const timer = setTimeout(() => {
        autoSaveTimers.delete(target);
        // Re-check inside the timeout in case the DOM changed
        const stillInDom = document.body.contains(target);
        if (!stillInDom) return;
        saveInitials(target);
      }, 400); // 400ms pause after the 2nd character
      autoSaveTimers.set(target, timer);
    }
  });

  // Edit button → toggle the collapsed initials editor instead of navigating to the product page.
  // Registered in CAPTURE phase so it runs before any other click handler (including the theme's
  // default anchor navigation behavior). Using capture ensures we can reliably preventDefault.
  document.addEventListener('click', (event) => {
    const editBtn = event.target && event.target.closest && event.target.closest('[data-toggles-initials="true"]');
    if (!editBtn) return;

    // Find the initials editor in the same cart item
    const cartItem = editBtn.closest('.cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]');
    if (!cartItem) return;
    const editor = cartItem.querySelector('.cart-item__initials-edit');
    if (!editor) return; // No editor in this row — allow normal anchor navigation

    // We have an editor to toggle. Always block navigation.
    event.preventDefault();
    event.stopPropagation();

    const isCollapsed = editor.classList.contains('is-collapsed');
    if (isCollapsed) {
      editor.classList.remove('is-collapsed');
      editBtn.setAttribute('aria-expanded', 'true');
      const input = editor.querySelector('.cart-item__initials-input');
      if (input) {
        setTimeout(() => {
          try { input.focus(); input.select(); } catch (e) { /* ignore */ }
        }, 50);
      }
    } else {
      editor.classList.add('is-collapsed');
      editBtn.setAttribute('aria-expanded', 'false');
    }
  }, true); // <-- true = capture phase

  // Also emit "Saved ✓" on the success path of saveInitials (debug report flagged this as dead code).
  // We can't cleanly splice it in from here, so we hook the cartUpdate pubsub to flash on the newly-
  // rendered editor when it's still visible (e.g. empty value after clearing).
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source !== 'cart-items') return;
        // Briefly flash "Saved ✓" on any visible (not collapsed) editor
        document.querySelectorAll('.cart-item__initials-edit:not(.is-collapsed) .cart-item__initials-status').forEach((el) => {
          el.textContent = SAVED_MESSAGE;
          el.classList.remove('is-error');
          el.classList.add('is-success');
          setTimeout(() => {
            if (el.textContent === SAVED_MESSAGE) {
              el.textContent = '';
              el.classList.remove('is-success');
            }
          }, 1800);
        });
      });
    }
  } catch (e) { /* non-fatal */ }

  /* ---- Inject the initials editor for APP-ADDED cart lines ----
     cart-drawer.liquid only renders the editor when the line item carries the initials
     property. Theme adds always post that field (even empty), but app adds (Fast Bundle
     bundles) carry no properties at all, so bundle-added personalised products had no
     way to add initials in the drawer. preorder-labels.js calls this hook for every
     drawer row it walks; we confirm the product really is Zepto-personalised (config
     probe, cached per product id in sessionStorage), then inject the exact editor
     markup the theme renders. The existing document-level input/change listeners pick
     it up with zero extra wiring, and the save writes the canonical initials key. */
  let persCache = {};
  try { persCache = JSON.parse(sessionStorage.getItem('sc_zepto_pers') || '{}'); } catch (e) { persCache = {}; }
  function zeptoPersonalized(pid, cb) {
    const key = String(pid);
    if (Object.prototype.hasOwnProperty.call(persCache, key)) { cb(persCache[key]); return; }
    fetch('https://cdn-zeptoapps.com/product-personalizer/canvas-script.php?shop=scarters.myshopify.com&prid=' + pid + '&kkr=tomato')
      .then((r) => r.text())
      .then((t) => {
        const pers = /cstmfy_meta_\d+/.test(t) && /initial/i.test(t);
        persCache[key] = pers;
        try { sessionStorage.setItem('sc_zepto_pers', JSON.stringify(persCache)); } catch (e) {}
        cb(pers);
      })
      .catch(() => { cb(false); });
  }
  window.__scEnsureInitialsEditor = function (row, item, line) {
    if (!row || row.querySelector('.cart-item__initials-edit')) return;
    if (!item || !item.product_id) return;
    zeptoPersonalized(item.product_id, function (pers) {
      if (!pers) return;
      if (row.querySelector('.cart-item__initials-edit')) return; // re-check after the async probe
      const host = row.querySelector('.cart-item__details .col-9') || row.querySelector('.col-9');
      if (!host) return;
      const wrap = document.createElement('div');
      wrap.className = 'cart-item__initials-edit';
      wrap.setAttribute('data-line', String(line));
      wrap.setAttribute('data-initials-key', 'Add Initials (Optional)');
      const lab = document.createElement('label');
      lab.className = 'cart-item__initials-label';
      lab.setAttribute('for', 'CartDrawer-Initials-' + line);
      lab.textContent = 'Add Initials (Optional)';
      const rowDiv = document.createElement('div');
      rowDiv.className = 'cart-item__initials-row';
      const inp = document.createElement('input');
      inp.type = 'text';
      inp.id = 'CartDrawer-Initials-' + line;
      inp.className = 'cart-item__initials-input';
      inp.maxLength = 2;
      inp.autocomplete = 'off';
      inp.setAttribute('data-line', String(line));
      inp.setAttribute('data-original', '');
      const status = document.createElement('span');
      status.className = 'cart-item__initials-status';
      status.setAttribute('aria-live', 'polite');
      rowDiv.appendChild(inp);
      rowDiv.appendChild(status);
      const help = document.createElement('span');
      help.className = 'cart-item__initials-help';
      help.textContent = 'Add 2 initials. No returns on personalised items.';
      wrap.appendChild(lab);
      wrap.appendChild(rowDiv);
      wrap.appendChild(help);
      const qty = host.querySelector('.cart-item__quantity');
      if (qty) host.insertBefore(wrap, qty); else host.appendChild(wrap);
    });
  };

  /* ---- Stamp properties[Pre-Order]=Yes onto APP-ADDED lines ----
     Theme adds carry the property from the product form, so it reaches the ORDER for
     fulfilment. App adds (Fast Bundle bundles) carry no properties, so the order line
     had no pre-order flag. preorder-labels detects handle-level pre-order lines and
     calls this hook; we merge the property onto the line via XHR, which bypasses Fast
     Bundle's fetch wrapper (it re-mangles /cart/change for bundle items). */
  const stampInFlight = {};
  function stampOnce(item, attempt) {
    const props = Object.assign({}, item.properties || {});
    if (props['Pre-Order']) { delete stampInFlight[item.key]; return; }
    props['Pre-Order'] = 'Yes';
    Object.keys(props).forEach(function (k) {
      if (props[k] === '' || props[k] === null || props[k] === undefined) delete props[k];
    });
    const x = new XMLHttpRequest();
    x.open('POST', '/cart/change.js');
    x.setRequestHeader('Content-Type', 'application/json');
    x.onerror = function () { delete stampInFlight[item.key]; };
    x.onload = function () {
      if (x.status >= 400) { delete stampInFlight[item.key]; return; }
      /* Fast Bundle rewrites the bundle's lines shortly after its add (applying the
         bundle discount), which can wipe a stamp that lands inside that window - the
         first live test proved it. So VERIFY after a settle delay and retry with
         backoff until the property survives FB's post-add processing. */
      setTimeout(function () {
        const v = new XMLHttpRequest();
        v.open('GET', '/cart.js?scstamp=' + attempt + '-' + Date.now());
        v.onerror = function () { delete stampInFlight[item.key]; };
        v.onload = function () {
          try {
            const cart = JSON.parse(v.responseText);
            /* A property change ROTATES the line-item key (the key hashes the
               properties), so after a successful stamp the old key matches nothing.
               Fall back to variant_id so the line stays trackable across rotations
               and the chain only ends once the property actually STICKS. */
            let fresh = (cart.items || []).filter(function (it) { return it.key === item.key; })[0];
            if (!fresh) fresh = (cart.items || []).filter(function (it) { return it.variant_id === item.variant_id; })[0];
            if (fresh && !(fresh.properties || {})['Pre-Order'] && attempt < 6) {
              stampOnce(fresh, attempt + 1);
            } else {
              delete stampInFlight[item.key];
            }
          } catch (err) { delete stampInFlight[item.key]; }
        };
        v.send();
      }, 2500 * attempt);
    };
    x.send(JSON.stringify({ id: item.key, quantity: item.quantity, properties: props }));
  }
  window.__scStampPreorder = function (item) {
    if (!item || !item.key || stampInFlight[item.key]) return;
    if ((item.properties || {})['Pre-Order']) return;
    stampInFlight[item.key] = true;
    try { stampOnce(item, 1); } catch (err) { delete stampInFlight[item.key]; }
  };
})();
/* ============ /Cart item initials editor ============ */